import React from 'react';
import { useMutation } from '@apollo/client';
import { Button } from '@artstorefronts/ui';
import { MinusCircle, PlusCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FOLLOW_MUTATION, UNFOLLOW_MUTATION } from '~/graphql/mutations';
import { useAnalytics } from '~/context/analytics';
import { HoverExplainer } from '~/components/HoverExplainer';
import { pop } from '~/utils/celebrate';
import { toast } from 'sonner';

/**
 * One follow relationship, one request in flight — however many controls show it.
 *
 * An artist can own several controls at once (two notification rows, a suggestion card,
 * the unfollow hint under each), and every one of them reads the same override entry.
 * The optimistic flip lands the moment the first request leaves, so all of them turn
 * over while it is still open. Owning the mutations and the pending set here rather than
 * per control is what stops a tap on the second control from sending the OPPOSITE
 * mutation into the same race — two responses landing in arbitrary order left the caller
 * showing the loser and disagreeing with the server.
 *
 * Extracted from the notifications page so the network hub can follow from a suggestion
 * card without a second, subtly different implementation of the same race.
 *
 * Emits the canonical sn_user_followed / sn_user_unfollowed pair. Town-square has fired
 * those since it shipped; the client app fired nothing at all, so following from a
 * notification row has been invisible in Amplitude. Centralising it here is what makes
 * the two apps comparable rather than adding a third per-surface event name.
 */
export function useFollowActions(surface: string) {
  const { amplitude } = useAnalytics();
  const { t } = useTranslation('social');
  const [followOverrides, setFollowOverrides] = React.useState<Record<string, boolean>>({});
  const [followPending, setFollowPending] = React.useState<Record<string, boolean>>({});
  // A ref, not the state above: a second tap can arrive before React has re-rendered the
  // sibling controls with their button disabled, and the guard has to hold on that tap.
  const inFlight = React.useRef<Set<string>>(new Set());
  const [followMutation] = useMutation(FOLLOW_MUTATION);
  const [unfollowMutation] = useMutation(UNFOLLOW_MUTATION);

  // True only when this call's mutation succeeded. Already-in-flight and a refused
  // write resolve false — the promise never rejects, so a caller recording a conversion
  // has to read the boolean rather than treating resolution as success.
  const setFollowing = React.useCallback(
    // `surfaceOverride` lets a shared owner (the hover-card provider) report the page that
    // actually tapped, so one owner per app does not collapse every surface into one name.
    async (userId: string, next: boolean, surfaceOverride?: string): Promise<boolean> => {
      if (inFlight.current.has(userId)) return false;
      inFlight.current.add(userId);
      setFollowPending((prev) => ({ ...prev, [userId]: true }));
      // The override IS the optimistic UI — every control for this artist reads it.
      setFollowOverrides((prev) => ({ ...prev, [userId]: next }));

      amplitude.track(next ? 'sn_user_followed' : 'sn_user_unfollowed', {
        target_user_id: userId,
        surface: surfaceOverride ?? surface,
      });

      try {
        if (next) {
          await followMutation({ variables: { userId } });
        } else {
          await unfollowMutation({ variables: { userId } });
        }
        return true;
      } catch (err) {
        // The override is rolled back below, so without a word to the reader the control
        // simply returns to "Follow" and the tap reads as ignored rather than failed.
        // Every refusal the server makes here is one a person can act on. These mutations
        // deliberately carry no onError option — Apollo swallows the rejection when one is
        // present, and then this catch never runs.
        console.error('Follow action failed:', err);
        toast.error(next ? t('follow-failed') : t('unfollow-failed'));
        setFollowOverrides((prev) => ({ ...prev, [userId]: !next }));
        return false;
      } finally {
        inFlight.current.delete(userId);
        setFollowPending((prev) => ({ ...prev, [userId]: false }));
      }
    },
    [amplitude, followMutation, surface, t, unfollowMutation]
  );

  // Drops the optimistic overrides, for an owner that outlives one page. Requests still in
  // flight keep their pending guard and settle as usual.
  function reset() {
    setFollowOverrides({});
  }

  return { followOverrides, followPending, setFollowing, reset };
}

export interface FollowButtonProps {
  userId: string;
  isFollowing: boolean;
  isFollowBack?: boolean;
  /** True while this artist's follow state is being written by ANY control — see useFollowActions. */
  isPending: boolean;
  onToggle: (userId: string, following: boolean) => void;
  /** Override the default colour. The network hub runs green to match its Connect CTA; the profile hover card fills it. */
  tone?: 'indigo' | 'green' | 'solid';
  /** Stretch to the container at every breakpoint instead of shrinking to the label from `sm` up. */
  fullWidth?: boolean;
  /** Skip the click pop and the follow explainer popover. The profile hover card sets it: a popover over a popover. */
  quiet?: boolean;
  className?: string;
}

const TONES: Record<NonNullable<FollowButtonProps['tone']>, string> = {
  indigo:
    'bg-transparent text-indigo-600 border border-indigo-300 hover:bg-indigo-600 hover:text-white hover:border-indigo-600',
  green:
    'bg-transparent text-green-700 border border-green-300 hover:bg-green-600 hover:text-white hover:border-green-600',
  solid:
    'bg-indigo-600 text-white border border-indigo-600 hover:bg-indigo-700 hover:text-white hover:border-indigo-700',
};

export function FollowButton({
  userId,
  isFollowing,
  isFollowBack,
  isPending,
  onToggle,
  tone = 'indigo',
  fullWidth,
  quiet,
  className,
}: FollowButtonProps) {
  const { t } = useTranslation('social');
  const controlRef = React.useRef<HTMLDivElement>(null);

  function handleClick() {
    if (isPending) return;
    // The lighter half of the delight pair. A follow is a transaction rather than a
    // gesture, so it answers the finger without spending the confetti — but it does
    // answer, because a one-tap control that does nothing visible reads as broken.
    if (!quiet) pop(controlRef.current);
    onToggle(userId, !isFollowing);
  }

  const showFollowHover = !isFollowing && !quiet;

  // Keep the pop target outside HoverExplainer. Toggling `enabled` swaps
  // HoverCard for the raw child, which remounts the subtree and would
  // cancel the Web Animations scale before paint.
  return (
    <div ref={controlRef} className={className ?? (fullWidth ? 'w-full' : 'w-full sm:w-auto')}>
      <HoverExplainer
        enabled={showFollowHover}
        icon={PlusCircle}
        title={t('follow-hover-title')}
        text={t('follow-hover-text')}
      >
        <Button
          variant="outline"
          size="sm"
          className={`${fullWidth ? 'w-full' : 'w-full sm:w-auto'} whitespace-nowrap rounded-[8px] gap-1.5 ${
            isFollowing
              ? 'bg-transparent text-gray-700 border border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
              : TONES[tone]
          } ${isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={isPending}
          onClick={handleClick}
          data-testid="follow-button"
        >
          {isFollowing ? <MinusCircle className="w-4 h-4" /> : <PlusCircle className="w-4 h-4" />}
          <span>{isFollowing ? t('following') : isFollowBack ? t('connection.follow-back') : t('follow')}</span>
        </Button>
      </HoverExplainer>
    </div>
  );
}
